import { useEffect, useRef, useState } from 'react';
import { useT, type Key } from '../i18n';

const isTouchDevice =
  typeof window !== 'undefined' &&
  ('ontouchstart' in window ||
    (navigator.maxTouchPoints !== undefined && navigator.maxTouchPoints > 0));

const TOUCH_INSTRUCTIONS: { keys: Key; desc: Key }[] = [
  { keys: 'help_drag', desc: 'help_drag_desc' },
  { keys: 'help_pinch', desc: 'help_pinch_desc' },
  { keys: 'help_two_finger', desc: 'help_two_finger_desc' },
  { keys: 'help_tap_lot', desc: 'help_tap_lot_desc' },
];

const DESKTOP_INSTRUCTIONS: { keys: Key; desc: Key }[] = [
  { keys: 'help_click', desc: 'help_click_desc' },
  { keys: 'help_esc', desc: 'help_esc_desc' },
  { keys: 'help_arrows', desc: 'help_arrows_desc' },
  { keys: 'help_alt', desc: 'help_alt_desc' },
  { keys: 'help_cmd', desc: 'help_cmd_desc' },
  { keys: 'help_wheel', desc: 'help_wheel_desc' },
  { keys: 'help_space', desc: 'help_space_desc' },
  { keys: 'help_click_lot', desc: 'help_click_lot_desc' },
];

export function HUD() {
  const { t } = useT();
  // Start open on first visit so the controls are discoverable. The
  // click-outside effect below dismisses the popup as soon as the visitor
  // interacts with the map.
  const [open, setOpen] = useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const items = isTouchDevice ? TOUCH_INSTRUCTIONS : DESKTOP_INSTRUCTIONS;

  // Click outside the help popup → close it.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = containerRef.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  return (
    <>
      {/* Center crosshair (desktop only — meaningless without a hidden cursor). */}
      {!isTouchDevice && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.85)',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.6)',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Help button + popup, top-right. */}
      <div
        ref={containerRef}
        style={{
          position: 'absolute',
          top: 52,            // 36 px sticky warning banner + 16 px gap
          right: 16,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          zIndex: 5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 8,
        }}
      >
        <button
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? 'Hide instructions' : 'Show instructions'}
          title={t('controls')}
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.25)',
            background: 'rgba(0,0,0,0.62)',
            color: 'white',
            cursor: 'pointer',
            fontSize: 16,
            fontWeight: 600,
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
        >
          {open ? '✕' : '?'}
        </button>

        {open && (
          <div
            style={{
              padding: '10px 14px',
              background: 'rgba(0,0,0,0.62)',
              color: 'white',
              borderRadius: 8,
              fontSize: 12,
              lineHeight: 1.7,
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              minWidth: 220,
              maxWidth: 'calc(100vw - 32px)',
            }}
          >
            <div
              style={{
                fontSize: 10,
                opacity: 0.55,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                marginBottom: 6,
              }}
            >
              {t('controls')}
            </div>
            {items.map((it) => (
              <div
                key={it.keys}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}
              >
                <span style={{ fontWeight: 600 }}>{t(it.keys)}</span>
                <span style={{ opacity: 0.78 }}>{t(it.desc)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* The dev "send to phone" button now lives in PhoneUrlButton.tsx so
          it stays visible in both Cesium and Leaflet modes. */}
    </>
  );
}
